import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
const easeOut = [0.16, 1, 0.3, 1] as const;
export function HeroSection() {
  const {
    lang
  } = useLanguage();
  const trust = [{
    value: '100+',
    label: lang === 'es' ? 'Negocios construidos' : 'Businesses built'
  }, {
    value: '100%',
    label: lang === 'es' ? 'Estrategia a medida' : 'Tailored Strategy'
  }, {
    value: '<2 sem',
    valueEn: '<2 wks',
    label: lang === 'es' ? 'Entrega promedio' : 'Average Delivery'
  }];
  const fadeUp = (delay: number) => ({
    initial: {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.6,
      ease: easeOut,
      delay
    }
  });
  const fade = (delay: number, duration = 0.6) => ({
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    transition: {
      duration,
      ease: easeOut,
      delay
    }
  });
  return <section id="inicio" className="relative bg-[#0F0A1E] w-full lg:min-h-[100vh] overflow-hidden" data-id="element-212">
      <div className="grid grid-cols-1 lg:grid-cols-[52%_48%] lg:min-h-[100vh]" data-id="element-213">
        {/* ───────────────────────── PHOTO COLUMN ───────────────────────── */}
        <motion.div {...fade(0, 1.2)} className="relative order-1 lg:order-2 w-full h-[70vw] sm:h-[60vw] lg:h-[100vh] overflow-hidden bg-[#13102a] flex items-end justify-center" data-id="element-214">
          {/* Subtle purple radial glow behind the figure */}
          <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[70%] z-0" style={{
          background: 'radial-gradient(ellipse, rgba(107,63,160,0.20) 0%, transparent 70%)'
        }} data-id="element-215" />

          <img src="https://cdn.magicpatterns.com/uploads/8wA1hfb42Whxpi23trRcAX/anny_png.png" alt="Annysol Navarro, fundadora de Inkova Creation" loading="eager" className="relative z-[1] w-full h-[95%] object-contain [object-position:bottom_center]" style={{
          filter: 'drop-shadow(0 20px 40px rgba(61, 41, 112, 0.25))'
        }} data-id="element-216" />

          {/* Year stamp — editorial detail */}
          <span className="absolute bottom-10 right-8 z-[3] hidden lg:block font-sans text-[11px] tracking-[3px] text-[#C084FC]/40" data-id="element-217">
            EST. 2018 · MIAMI
          </span>
        </motion.div>

        {/* ───────────────────────── TEXT COLUMN ───────────────────────── */}
        <div className="relative order-2 lg:order-1 z-[2] flex flex-col justify-center bg-[#0F0A1E] px-6 py-12 pb-16 lg:py-0 lg:pl-[7vw] lg:pr-12" data-id="element-218">
          {/* 1. Eyebrow label */}
          <motion.div {...fade(0.1)} className="mb-6" data-id="element-219">
            <span className="inline-block align-middle w-[32px] h-[2px] bg-[#C084FC] mr-3" data-id="element-220" />
            <span className="align-middle font-sans text-[11px] font-medium tracking-[3px] uppercase text-[#C084FC]" data-id="element-221">
              {lang === 'es' ? 'Miami · Agencia Digital' : 'Miami · Digital Agency'}
            </span>
          </motion.div>

          {/* 2. H1 */}
          <h1 className="font-display text-[34px] lg:text-[52px] font-bold mb-6" style={{
          lineHeight: 1.15
        }} data-id="element-222">
            {lang === 'es' ? <>
                <motion.span {...fadeUp(0.2)} className="block text-white tracking-[-0.5px] lg:tracking-[-1px]" data-id="element-223">
                  Tu negocio digital,
                </motion.span>
                <motion.span {...fadeUp(0.35)} className="block text-white tracking-[-0.5px] lg:tracking-[-1px]" data-id="element-224">
                  construido desde tu
                </motion.span>
                <motion.span {...fadeUp(0.5)} className="block italic font-bold text-[#C084FC] tracking-[-0.5px] lg:tracking-[-1px]" data-id="element-225">
                  identidad y propósito.
                </motion.span>
              </> : <>
                <motion.span {...fadeUp(0.2)} className="block text-white tracking-[-0.5px] lg:tracking-[-1px]" data-id="element-226">
                  Your digital business,
                </motion.span>
                <motion.span {...fadeUp(0.35)} className="block text-white tracking-[-0.5px] lg:tracking-[-1px]" data-id="element-227">
                  built from your
                </motion.span>
                <motion.span {...fadeUp(0.5)} className="block italic font-bold text-[#C084FC] tracking-[-0.5px] lg:tracking-[-1px]" data-id="element-228">
                  identity and purpose.
                </motion.span>
              </>}
          </h1>

          {/* 3. Subtitle */}
          <motion.p {...fade(0.6)} className="font-sans text-[15px] lg:text-[16px] text-white/65 max-w-[440px] mb-10" style={{
          lineHeight: 1.8
        }} data-id="element-229">
            {lang === 'es' ? 'Diseñamos negocios digitales con claridad, estrategia e intención. Tu presencia digital debe comunicar tu valor antes de que hables.' : 'We design digital businesses with clarity, strategy and intention. Your digital presence should communicate your value before you speak.'}
          </motion.p>

          {/* 4. Divider line */}
          <motion.div {...fade(0.7)} className="w-[48px] h-[1px] mb-8" style={{
          background: 'rgba(192,132,252,0.3)'
        }} data-id="element-230" />

          {/* 5. CTA buttons */}
          <motion.div {...fade(0.75)} className="flex flex-col sm:flex-row sm:items-start gap-y-2 gap-x-4" data-id="element-231">
            <div className="flex flex-col items-stretch sm:items-start" data-id="element-232">
              <a href="https://calendly.com/inkovacreation" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-[#6B3FA0] text-white font-sans font-semibold text-[13px] tracking-[0.8px] uppercase px-9 py-4 rounded-[50px] transition-all duration-300 hover:bg-[#3D2970] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(107,63,160,0.35)]" data-id="element-233">
                {lang === 'es' ? 'Da el primer paso' : 'Take the first step'}
              </a>
              <span className="font-sans text-[11px] tracking-[0.5px] text-white/45 mt-2.5 text-center sm:text-left" data-id="element-234">
                {lang === 'es' ? 'Sesión Diagnóstico · $50 · 30 min' : 'Diagnostic Session · $50 · 30 min'}
              </span>
            </div>
            <a href="https://wa.me/13054781618" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-transparent text-[#C084FC] border-[1.5px] border-[#6B3FA0] font-sans font-medium text-[13px] tracking-[0.8px] uppercase px-8 py-[15px] rounded-[50px] transition-all duration-300 hover:bg-[#6B3FA0]/10 hover:-translate-y-0.5" data-id="element-235">
              {lang === 'es' ? 'Tengo una pregunta' : 'I have a question'}
            </a>
          </motion.div>

          {/* 6. Trust indicators */}
          <motion.div {...fade(0.9)} className="grid grid-cols-3 gap-4 sm:flex sm:items-center sm:gap-10 mt-10 lg:mt-14" data-id="element-236">
            {trust.map((item, index) => <Fragment key={index} data-id="element-237">
                <div className="flex flex-col items-center text-center sm:items-start sm:text-left" data-id="element-238">
                  <span className="font-display font-bold text-[28px] lg:text-[36px] text-white leading-none" data-id="element-239">
                    {lang === 'en' && item.valueEn ? item.valueEn : item.value}
                  </span>
                  <span className="font-sans text-[11px] tracking-[1.5px] uppercase text-white/45 mt-1" data-id="element-240">
                    {item.label}
                  </span>
                </div>
                {index < trust.length - 1 && <span className="hidden sm:block w-[1px] h-[40px]" style={{
              background: 'rgba(192,132,252,0.20)'
            }} data-id="element-241" />}
              </Fragment>)}
          </motion.div>
        </div>
      </div>
    </section>;
}